import { Button } from "@babackapp/ui/components/button";
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@babackapp/ui/components/dialog";
import { Alert, AlertDescription, AlertTitle } from "@babackapp/ui/components/alert";
import { Badge } from "@babackapp/ui/components/badge";
import { Switch } from "@babackapp/ui/components/switch";
import { useTranslation } from "react-i18next";
import { tracking } from "@babackapp/transactional-emails/lib";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { getTrackingStatusTranslation, TrackingStatus } from '@babackapp/tracking-status-registry'

export interface ConfirmationStepProps {
  selectedStatuses: string[];
  onBack: () => void;
  onConfirm: (enableWorkflows: boolean) => void;
}


export function ConfirmationStep({ selectedStatuses, onBack, onConfirm }: ConfirmationStepProps) {
  const { t, i18n } = useTranslation("trackoffice");
  const locale = i18n.language || "en";
  const [enableWorkflows, setEnableWorkflows] = useState(false);

  const templatesList = Object.entries(tracking.defaultEmailTemplates) as [
    keyof typeof tracking.defaultEmailTemplates,
    (typeof tracking.defaultEmailTemplates)[keyof typeof tracking.defaultEmailTemplates],
  ][];

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {t(
            "templatesEditor.createDefaultTemplates.confirmTitle",
            "Confirm template creation",
          )}
        </DialogTitle>
        <DialogDescription>
          {t(
            "templatesEditor.createDefaultTemplates.confirmDescription",
            "Review your selection before creating the templates",
          )}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-medium mb-2">
            {t(
              "templatesEditor.createDefaultTemplates.selectedStatuses",
              "Selected statuses",
            )} ({selectedStatuses.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {selectedStatuses.map((statusType) => {
              const template = templatesList.find(([_, t]) => t.type === statusType);
              return (
                <Badge key={statusType} variant="secondary">
                  {template ? getTrackingStatusTranslation(template[0] as TrackingStatus, locale).internalTitle : statusType}
                </Badge>
              );
            })}
          </div>
        </div>

        <Alert variant="warning">
          <AlertTitle>
            {t(
              "templatesEditor.createDefaultTemplates.workflowsTitle",
              "Workflow activation",
            )}
          </AlertTitle>
          <AlertDescription className="space-y-3">
            <p>
              {t(
                "templatesEditor.createDefaultTemplates.workflowsDescription",
                "You can enable the workflows immediately after creation.",
              )}
            </p>
            <div className="flex items-center gap-3">
              <Switch
                id="enable-workflows"
                checked={enableWorkflows}
                onCheckedChange={setEnableWorkflows}
              />
              <label
                htmlFor="enable-workflows"
                className="text-sm font-medium leading-none cursor-pointer"
              >
                {t(
                  "templatesEditor.createDefaultTemplates.enableWorkflows",
                  "Enable workflows after creation",
                )}
              </label>
            </div>
          </AlertDescription>
        </Alert>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t("templatesEditor.createDefaultTemplates.back", "Back")}
        </Button>
        <Button onClick={() => onConfirm(enableWorkflows)}>
          {t("templatesEditor.createDefaultTemplates.confirm", "Confirm")}
        </Button>
      </DialogFooter>
    </>
  );
}
